import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { productsService } from '../services/api';
import priceScraperService from '../services/priceScraperService';

const PriceComparison = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [days, setDays] = useState(30);
  const [testUrl, setTestUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsService.getAll();
      // Filtrer uniquement les produits avec des URLs concurrents
      const productsWithUrls = response.data.filter(p => p.competitorUrls && p.competitorUrls.length > 0);
      setProducts(productsWithUrls);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError('Erreur lors du chargement des produits');
    }
  };

  const fetchPriceHistory = async (productId) => {
    try {
      setLoading(true);
      setError('');
      const response = await priceScraperService.getProductPriceHistory(productId, days);
      setPriceHistory(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setError('Erreur lors du chargement de l\'historique des prix');
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    setSelectedProduct(product);
    if (product) {
      fetchPriceHistory(product.id);
    }
  };

  const handleScrapeNow = async () => {
    try {
      setScraping(true);
      setError('');
      setSuccess('');
      const response = await priceScraperService.scrapeNow();
      setSuccess(`Scraping terminé : ${response.data.success} succès, ${response.data.failed} échecs`);
      
      // Recharger l'historique si un produit est sélectionné
      if (selectedProduct) {
        setTimeout(() => fetchPriceHistory(selectedProduct.id), 2000);
      }
    } catch (error) {
      console.error('Erreur lors du scraping:', error);
      setError('Erreur lors du scraping des prix');
    } finally {
      setScraping(false);
    }
  };

  const handleTestUrl = async () => {
    try {
      setTesting(true);
      setError('');
      setTestResult(null);
      const response = await priceScraperService.testUrl(testUrl);
      setTestResult(response.result);
      if (response.result.priceTtc) {
        setSuccess(`Prix trouvé : ${response.result.priceTtc}€ TTC`);
      } else {
        setError(`Erreur : ${response.result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors du test:', error);
      setError('Erreur lors du test de l\'URL');
    } finally {
      setTesting(false);
    }
  };

  const handleAnalyzeUrl = async () => {
    try {
      setAnalyzing(true);
      setError('');
      setAnalysisResult(null);
      const response = await priceScraperService.analyzeUrl(testUrl);
      setAnalysisResult(response.analysis);
      if (response.analysis.hasMultiplePrices) {
        setSuccess(`${response.analysis.prices.length} variantes détectées`);
      } else {
        setSuccess('Aucune variante multiple détectée');
      }
    } catch (error) {
      console.error('Erreur lors de l\'analyse:', error);
      setError('Erreur lors de l\'analyse de l\'URL');
    } finally {
      setAnalyzing(false);
    }
  };

  // Convertir en nombre de manière sûre
  const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!priceHistory || priceHistory.length === 0) return [];

    // Grouper par date
    const dataByDate = {};
    
    priceHistory.forEach(entry => {
      const date = new Date(entry.scrapedAt).toLocaleDateString('fr-FR');
      
      if (!dataByDate[date]) {
        dataByDate[date] = {
          date,
          ourPrice: toNumber(entry.ourPriceHt),
        };
      }
      
      // Ajouter le prix du concurrent
      if (entry.competitorPriceHt) {
        dataByDate[date][entry.competitorName] = toNumber(entry.competitorPriceHt);
      }
    });

    return Object.values(dataByDate).sort((a, b) => {
      const dateA = new Date(a.date.split('/').reverse().join('-'));
      const dateB = new Date(b.date.split('/').reverse().join('-'));
      return dateA - dateB;
    });
  };

  // Obtenir la liste des concurrents uniques
  const getCompetitors = () => {
    const competitors = new Set();
    priceHistory.forEach(entry => {
      if (entry.competitorName) {
        competitors.add(entry.competitorName);
      }
    });
    return Array.from(competitors);
  };

  // Calculer les statistiques
  const calculateStats = () => {
    if (!priceHistory || priceHistory.length === 0) return null;

    const latestData = {};
    priceHistory.forEach(entry => {
      if (!latestData[entry.competitorName] || new Date(entry.scrapedAt) > new Date(latestData[entry.competitorName].scrapedAt)) {
        // Convertir les valeurs en nombres
        latestData[entry.competitorName] = {
          ...entry,
          ourPriceHt: toNumber(entry.ourPriceHt),
          competitorPriceHt: entry.competitorPriceHt ? toNumber(entry.competitorPriceHt) : null,
          competitorPriceTtc: entry.competitorPriceTtc ? toNumber(entry.competitorPriceTtc) : null,
        };
      }
    });

    return Object.values(latestData);
  };

  const chartData = prepareChartData();
  const competitors = getCompetitors();
  const stats = calculateStats();

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Comparaison des Prix Concurrents
        </Typography>
        <Button
          variant="contained"
          startIcon={scraping ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={handleScrapeNow}
          disabled={scraping}
        >
          {scraping ? 'Scraping en cours...' : 'Scraper maintenant'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Test d'URL */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'info.dark' }}>
            <Typography variant="h6" gutterBottom>
              🧪 Test de scraping d'URL
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="URL à tester"
                  placeholder="https://www.fredimixtattoo.com/..."
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                  disabled={testing}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleTestUrl}
                  disabled={!testUrl || testing}
                  startIcon={testing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                >
                  {testing ? 'Test en cours...' : 'Tester'}
                </Button>
              </Grid>
              {testResult && (
                <Grid item xs={12}>
                  <Alert severity={testResult.priceTtc ? 'success' : 'warning'}>
                    {testResult.priceTtc ? (
                      <>
                        <strong>Prix trouvé :</strong> {testResult.priceTtc}€ TTC
                        {' - '}
                        <strong>Disponible :</strong> {testResult.isAvailable ? 'Oui' : 'Non'}
                      </>
                    ) : (
                      <>
                        <strong>Erreur :</strong> {testResult.error}
                      </>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Analyse des variantes */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'warning.dark' }}>
            <Typography variant="h6" gutterBottom>
              🔍 Analyse des variantes d'URL
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="URL à analyser"
                  placeholder="https://laboutiquedutatoueur.fr/..."
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                  disabled={analyzing}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="warning"
                  onClick={handleAnalyzeUrl}
                  disabled={!testUrl || analyzing}
                  startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                >
                  {analyzing ? 'Analyse...' : 'Analyser'}
                </Button>
              </Grid>
              {analysisResult && (
                <Grid item xs={12}>
                  <Alert severity={analysisResult.hasMultiplePrices ? 'warning' : 'success'}>
                    {analysisResult.hasMultiplePrices ? (
                      <>
                        <strong>{analysisResult.prices.length} variantes détectées !</strong>
                        <br />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {analysisResult.recommendation}
                        </Typography>
                        {analysisResult.prices.map((variant, index) => (
                          <Box key={index} sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2">
                              <strong>{variant.price}€</strong> - {variant.selector}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {variant.context.substring(0, 100)}...
                            </Typography>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <>
                        <strong>Aucune variante multiple détectée</strong>
                        <br />
                        Cette URL peut être ajoutée directement.
                      </>
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Sélection du produit */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Sélectionner un produit"
                  value={selectedProduct?.id || ''}
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Sélectionner un produit</em>
                  </MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} - {product.competitorUrls?.length || 0} concurrent(s)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Période"
                  value={days}
                  onChange={(e) => {
                    setDays(e.target.value);
                    if (selectedProduct) {
                      fetchPriceHistory(selectedProduct.id);
                    }
                  }}
                >
                  <MenuItem value={7}>7 jours</MenuItem>
                  <MenuItem value={14}>14 jours</MenuItem>
                  <MenuItem value={30}>30 jours</MenuItem>
                  <MenuItem value={60}>60 jours</MenuItem>
                  <MenuItem value={90}>90 jours</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                {selectedProduct && (
                  <Chip
                    label={`Prix actuel HT: ${selectedProduct.priceHt}€`}
                    color="primary"
                    sx={{ fontSize: '1rem', p: 2 }}
                  />
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Statistiques */}
        {stats && stats.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Prix actuels des concurrents
            </Typography>
            <Grid container spacing={2}>
              {stats.map((stat, index) => {
                const priceDiff = stat.competitorPriceHt - stat.ourPriceHt;
                const percentDiff = ((priceDiff / stat.ourPriceHt) * 100).toFixed(1);
                const isLower = priceDiff < 0;

                return (
                  <Grid item xs={12} sm={6} md={4} key={stat.competitorName}>
                    <Card sx={{ 
                      border: 2, 
                      borderColor: isLower ? 'success.main' : 'error.main' 
                    }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {stat.competitorName}
                          </Typography>
                          {isLower ? (
                            <TrendingDownIcon color="success" />
                          ) : (
                            <TrendingUpIcon color="error" />
                          )}
                        </Box>
                        
                        <Typography variant="h4" color={isLower ? 'success.main' : 'error.main'}>
                          {stat.competitorPriceHt?.toFixed(2)}€ HT
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                          Prix TTC: {stat.competitorPriceTtc?.toFixed(2)}€
                        </Typography>
                        
                        <Box mt={2}>
                          <Chip
                            label={`${isLower ? '' : '+'}${priceDiff.toFixed(2)}€ (${percentDiff}%)`}
                            color={isLower ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          Dernière MAJ: {new Date(stat.scrapedAt).toLocaleString('fr-FR')}
                        </Typography>
                        
                        {!stat.isAvailable && (
                          <Chip label="Indisponible" color="warning" size="small" sx={{ mt: 1 }} />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}

        {/* Graphique */}
        {loading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : chartData.length > 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <ChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Évolution des prix HT
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ourPrice"
                    stroke="#1976d2"
                    strokeWidth={3}
                    name="Notre prix"
                  />
                  {competitors.map((competitor, index) => (
                    <Line
                      key={competitor}
                      type="monotone"
                      dataKey={competitor}
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      name={competitor}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        ) : selectedProduct ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Aucune donnée disponible pour ce produit. Lancez un scraping pour collecter les prix.
            </Alert>
          </Grid>
        ) : null}

        {/* Tableau détaillé */}
        {priceHistory.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Historique détaillé
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Concurrent</TableCell>
                      <TableCell>Notre Prix HT</TableCell>
                      <TableCell>Leur Prix TTC</TableCell>
                      <TableCell>Leur Prix HT</TableCell>
                      <TableCell>Différence</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priceHistory.slice(0, 50).map((entry, index) => {
                      const ourPrice = toNumber(entry.ourPriceHt);
                      const competitorPriceHt = entry.competitorPriceHt ? toNumber(entry.competitorPriceHt) : null;
                      const competitorPriceTtc = entry.competitorPriceTtc ? toNumber(entry.competitorPriceTtc) : null;
                      const diff = competitorPriceHt ? competitorPriceHt - ourPrice : 0;
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(entry.scrapedAt).toLocaleString('fr-FR')}
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {entry.competitorName}
                          </TableCell>
                          <TableCell>{ourPrice.toFixed(2)}€</TableCell>
                          <TableCell>
                            {competitorPriceTtc ? `${competitorPriceTtc.toFixed(2)}€` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {competitorPriceHt ? `${competitorPriceHt.toFixed(2)}€` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={competitorPriceHt ? `${diff > 0 ? '+' : ''}${diff.toFixed(2)}€` : 'N/A'}
                              color={diff < 0 ? 'success' : diff > 0 ? 'error' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {entry.isAvailable ? (
                              <Chip label="Disponible" color="success" size="small" />
                            ) : (
                              <Chip label="Indisponible" color="warning" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PriceComparison;

